import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useTable, useSortBy, useGlobalFilter, usePagination } from 'react-table';
import { useHistory, useParams } from 'react-router-dom';
import { Spinner, Button, Alert } from 'react-bootstrap';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { NoData } from '../../SharedComponents/NoData';
import PageTitle from '../../../layouts/PageTitle';
import { GlobalFilter } from '../../../components/AppsMenu/Quotes/GlobalFilter';
import Select from 'react-select';

import VatPeriodStepper from './VatPeriodStepper';

import {
  Calendar,
  AlertTriangle,
  CheckCircle2,
  Clock,
  CreditCard,
  Settings,
  FileText,
  RefreshCw,
  Loader2
} from 'lucide-react';

function VatReportingPeriods() {
  const { companyId } = useParams();
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const history = useHistory();

  // Use sterpper to handle vat period
  const [useStepper, setUseStepper] = useState(true);


  const fetchVatPeriods = useCallback(async (fiscalYearId) => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/reporting/vat-reporting-periods`),
        {
          params: { fiscalYearId },
          withCredentials: true
        }
      );
      if (response.data.success) {
        setPeriods(response.data.periods);
      } else {
        setError('Failed to load VAT reporting periods');
      }
    } catch (err) {
      setError('Error fetching VAT reporting periods');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyId]);

  const handleRefresh = () => {
    if (selectedFiscalYear) {
      setRefreshing(true);
      fetchVatPeriods(selectedFiscalYear.fiscal_year_id);
    }
  };

  useEffect(() => {
    const getFiscalYears = async () => {
      try {
        const result = await axios.get(getApiUrl(`${companyId}/accounting/fiscalyears`), { withCredentials: true });
        setFiscalYears(result.data.fiscalYears);
        if (result.data.fiscalYears.length > 0) {
          const latestFiscalYear = result.data.fiscalYears.sort((a, b) => b.year - a.year)[0];
          setSelectedFiscalYear(latestFiscalYear);
          await fetchVatPeriods(latestFiscalYear.fiscal_year_id);
        }
      } catch (err) {
        console.error('Error fetching fiscal years:', err);
        setFiscalYears([]);
        setError('Det gick inte att hämta räkenskapsår. Försök igen senare.');
      } finally {
        setLoading(false);
      }
    };

    getFiscalYears();
  }, [companyId, fetchVatPeriods]);

  const handleFiscalYearChange = async (selectedOption) => {
    setSelectedFiscalYear(selectedOption.value);
    setLoading(true);
    await fetchVatPeriods(selectedOption.value.fiscal_year_id);
  };

  // const handleRowClick = (periodId) => {
  //   history.push(`/${companyId}/vat-period/${periodId}`);
  // };

  const handleRowClick = (periodId) => {
    if (useStepper) {
      history.push(`/${companyId}/vat-period-stepper/${periodId}`);
    } else {
      history.push(`/${companyId}/vat-period/${periodId}`);
    }
  };

  // Helper function to check if a period is overdue
  const isOverdue = (period) => {
    const today = new Date();
    const dueDate = new Date(period.due_date);
    return period.state === 'open' && dueDate < today;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK'
    }).format(amount);
  };

  const COLUMNS = useMemo(() => [
    {
      Header: 'Period',
      accessor: 'start_date',
      Cell: ({ value, row }) => (
        <div className="d-flex align-items-center">
          <div
            className="me-3 d-flex align-items-center justify-content-center"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              backgroundColor: isOverdue(row.original) ? '#fee2e215' : '#3b82f615',
              color: isOverdue(row.original) ? '#dc2626' : '#3b82f6'
            }}
          >
            {isOverdue(row.original) ? <AlertTriangle size={18} /> : <FileText size={18} />}
          </div>
          <div>
            <div style={{ fontWeight: '600', fontSize: '0.875rem', color: isOverdue(row.original) ? '#dc2626' : '#3b82f6' }}>
              {new Date(value).toLocaleDateString('sv-SE')} - {new Date(row.original.end_date).toLocaleDateString('sv-SE')}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              Period {periods.findIndex(p => p.vat_reporting_period_id === row.original.vat_reporting_period_id) + 1}
            </div>
          </div>
        </div>
      ),
    },
    {
      Header: 'Moms att betala/få tillbaka',
      accessor: 'vat_total',
      Cell: ({ value }) => (
        <span style={{
          fontSize: '0.875rem',
          fontWeight: '600',
          color: value > 0 ? '#dc2626' : '#059669'
        }}>
          {formatCurrency(value)}
        </span>
      ),
    },
    {
      Header: 'Deklaration senast',
      accessor: 'due_date',
      Cell: ({ value, row }) => (
        <div>
          <span style={{
            fontSize: '0.875rem',
            fontWeight: isOverdue(row.original) ? '600' : '500',
            color: isOverdue(row.original) ? '#dc2626' : '#374151'
          }}>
            {new Date(value).toLocaleDateString('sv-SE')}
          </span>
          {isOverdue(row.original) && (
            <div style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: '500' }}>
              FÖRSENAD!
            </div>
          )}
        </div>
      ),
    },
    {
      Header: 'Status',
      accessor: 'state',
      Cell: ({ value, row }) => {
        const getStatusConfig = (status, isOverdueStatus) => {
          if (isOverdueStatus) {
            return { bg: '#fee2e2', color: '#991b1b', text: 'Försenad' };
          }

          switch (status) {
            case 'open':
              return { bg: '#fef3c7', color: '#92400e', text: 'Öppen' };
            case 'closed':
              return { bg: '#d1fae5', color: '#065f46', text: 'Stängd' };
            case 'submitted':
              return { bg: '#dbeafe', color: '#1e40af', text: 'Inlämnad' };
            default:
              return { bg: '#f3f4f6', color: '#374151', text: status };
          }
        };

        const config = getStatusConfig(value, isOverdue(row.original));

        return (
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: '500',
              padding: '0.25rem 0.75rem',
              borderRadius: '1rem',
              backgroundColor: config.bg,
              color: config.color,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}
          >
            {isOverdue(row.original) && <AlertTriangle size={12} />}
            {value === 'open' && !isOverdue(row.original) && <Clock size={12} />}
            {value === 'closed' && <CheckCircle2 size={12} />}
            {value === 'submitted' && <CheckCircle2 size={12} />}
            {config.text}
          </span>
        );
      },
    }
  ], [periods]);

  const tableInstance = useTable(
    {
      columns: COLUMNS,
      data: periods,
      initialState: { pageSize },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    gotoPage,
    pageCount,
    setPageSize: setTablePageSize,
    setGlobalFilter,
  } = tableInstance;

  const { globalFilter, pageIndex } = state;

  // Enhanced pagination controls
  const PaginationControls = () => (
    <div
      style={{
        backgroundColor: '#fff',
        borderTop: '1px solid #f1f5f9',
        padding: '1rem 1.5rem'
      }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <select
            className="form-select form-select-sm"
            style={{
              width: 'auto',
              fontSize: '0.875rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px'
            }}
            value={pageSize}
            onChange={e => {
              setPageSize(Number(e.target.value));
              setTablePageSize(Number(e.target.value));
            }}
          >
            {[10, 20, 30, 40, 50].map(size => (
              <option key={size} value={size}>{size} per sida</option>
            ))}
          </select>
          <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
            Visar {page.length} av {periods.length} momsperioder
          </span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            {'<<'}
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            style={{ fontSize: '0.75rem' }}
          >
            Föregående
          </Button>
          <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
            Sida {pageIndex + 1} av {pageOptions.length}
          </span>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => nextPage()}
            disabled={!canNextPage}
            style={{ fontSize: '0.75rem' }}
          >
            Nästa
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            {'>>'}
          </Button>
        </div>
      </div>
    </div>
  );

  if (loading && !refreshing) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .table td {
          vertical-align: middle !important;
        }
        
        .clickable-row {
          cursor: pointer;
        }
        
        .clickable-row:hover {
          background-color: #f8f9fa;
        }
        
        .btn-outline-secondary {
          border-color: #6c757d !important;
        }
        
        .btn-outline-secondary:hover {
          background-color: #6c757d !important;
          border-color: #6c757d !important;
          color: #fff !important;
        }
        
        /* Add a refresh button animation */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>

      <PageTitle motherMenu={"Rapporter"} activeMenu={"Momsrapport"} />

      <div className="col-12">
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {fiscalYears.length === 0 && !loading ? (
          <>
            <NoData message="Det finns inga räkenskapsår att visa. Skapa ett nytt räkenskapsår för att börja lägga till verifikationer." />
            <div className="d-flex justify-content-center p-4">
              <Button variant="primary" onClick={() => history.push(`/${companyId}/settings`)}>
                <Settings size={16} className="me-2" />
                Gå till Inställningar
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Enhanced header with fiscal year selector and refresh */}
            <div className="d-flex align-items-center justify-content-between mb-4">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center gap-2">
                  <Calendar size={20} className="text-primary" />
                  <span className="fw-medium">Räkenskapsår:</span>
                </div>
                <Select
                  value={{ value: selectedFiscalYear, label: selectedFiscalYear?.year }}
                  onChange={handleFiscalYearChange}
                  options={fiscalYears.map(fy => ({ value: fy, label: fy.year }))}
                  styles={{
                    control: (base) => ({
                      ...base,

                      border: '1px solid #dee2e6',
                      borderRadius: '6px',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#adb5bd'
                      }
                    })
                  }}
                />
              </div>

              {/*<Button
                variant="outline-secondary"
                onClick={handleRefresh}
                disabled={refreshing || !selectedFiscalYear}
                className="d-flex align-items-center gap-2"
              >
                <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
                Uppdatera
              </Button>*/}

              {/* Development Toggle */}
              <div className="d-flex align-items-center gap-3">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="stepperToggle"
                    checked={useStepper}
                    onChange={(e) => setUseStepper(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="stepperToggle">
                    {useStepper ? 'Stepper UI' : 'Accordion UI'}
                  </label>
                </div>

              </div>

            </div>

            {refreshing && (
              <div className="text-center mb-3">
                <Loader2 size={16} className="me-1 spin" />
                Uppdaterar momsperioder...
              </div>
            )}

            {periods.length === 0 && !loading ? (
              <NoData message="Det finns inga momsperioder att visa för det valda räkenskapsåret." />
            ) : (
              <>
                {/* Enhanced table card */}
                <div className="card shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                  <div className="card-body p-0">
                    {/* Search filter */}
                    <div className="p-4 border-bottom" style={{ backgroundColor: '#fafbfc' }}>
                      <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
                    </div>

                    <div className="table-responsive">
                      <table {...getTableProps()} className="table table-hover mb-0">
                        <thead style={{ backgroundColor: '#fff', borderBottom: '2px solid #f1f5f9' }}>
                          {headerGroups.map((headerGroup, headerGroupIndex) => (
                            <tr {...headerGroup.getHeaderGroupProps()} key={`header-group-${headerGroupIndex}`}>
                              {headerGroup.headers.map((column, columnIndex) => (
                                <th
                                  {...column.getHeaderProps(column.getSortByToggleProps())}
                                  key={`header-${columnIndex}-${column.id}`}
                                  className="fw-semibold border-0 py-4 px-4"
                                  style={{
                                    cursor: column.canSort ? 'pointer' : 'default',
                                    userSelect: 'none',
                                    fontSize: '0.875rem',
                                    letterSpacing: '0.025em',
                                    color: '#1e293b',
                                    backgroundColor: 'transparent'
                                  }}
                                >
                                  <div className="d-flex align-items-center justify-content-between">
                                    <span>{column.render('Header')}</span>
                                    {column.canSort && (
                                      <span className="ms-2">
                                        {column.isSorted ? (
                                          column.isSortedDesc ?
                                            <i className="fa fa-sort-down" style={{ fontSize: '0.75rem', color: '#3b82f6' }} />
                                            :
                                            <i className="fa fa-sort-up" style={{ fontSize: '0.75rem', color: '#3b82f6' }} />
                                        ) : (
                                          <i className="fa fa-sort" style={{ fontSize: '0.75rem', color: '#cbd5e1', opacity: '0.6' }} />
                                        )}
                                      </span>
                                    )}
                                  </div>
                                </th>
                              ))}
                            </tr>
                          ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                          {page.map((row) => {
                            prepareRow(row);
                            return (
                              <tr
                                {...row.getRowProps()}
                                key={row.id}
                                onClick={() => handleRowClick(row.original.vat_reporting_period_id)}
                                style={{
                                  cursor: "pointer",
                                  transition: 'all 0.2s ease',
                                  borderBottom: '1px solid #f1f5f9'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#f8fafc';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                              >
                                {row.cells.map(cell => (
                                  <td
                                    {...cell.getCellProps()}
                                    key={cell.column.id}
                                    className="py-4 px-4 align-middle border-0"
                                    style={{
                                      fontSize: '0.875rem',
                                      color: '#374151'
                                    }}
                                  >
                                    {cell.value !== null && cell.value !== undefined ? cell.render('Cell') : '-'}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {periods.length > pageSize && <PaginationControls />}
                  </div>
                </div>

                {/* Overdue Periods Warning */}
                {periods.some(period => isOverdue(period)) && (
                  <Alert variant="danger" className="mt-3 border-0 shadow-sm">
                    <div className="d-flex align-items-start gap-3">
                      <AlertTriangle size={20} className="text-danger flex-shrink-0 mt-1" />
                      <div>
                        <Alert.Heading className="h6 mb-1">
                          Varning: Du har {periods.filter(period => isOverdue(period)).length} försenade momsperioder
                        </Alert.Heading>
                        <p className="mb-0 small">
                          Klicka på en försenad period för att hantera den omedelbart. Försenade momsdeklarationer kan medföra avgifter från Skatteverket.
                        </p>
                      </div>
                    </div>
                  </Alert>
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default VatReportingPeriods;
